import { CollectionType } from '@/enums/collection-type';
import { WebLocale } from '@/enums/web-locale';
import { RouteConfig } from 'vue-router/types/router';
import { NavigationGuardNext, Route } from 'vue-router';
import { PageType } from './config';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

////////////////////////
/* GUIDE LANDING PAGE */
////////////////////////

function makeEditorialSubdomainLandingRoute(): RouteConfig {
	return {
		path: `/`,
		name: 'app.editorial.subdomain.landing',
		component: () => import(/* webpackChunkName: "editorial" */ '@/features/editorial/pages/GuideLanding.vue'),
		beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext) => {
			const languageStore = useLanguageStore();
			await languageStore.fetchLocales();
			next();
		},
		meta: { tab: 'editorial', pageType: PageType.EDITORIAL, subdomain: Domain.EDITORIAL },
	};
}

////////////////////
/* GUIDE HOMEPAGE */
////////////////////

export const editorialSlugs = {
	[WebLocale.br]: 'guia',
	[WebLocale.de]: 'guide',
	[WebLocale.es]: 'guia',
	[WebLocale.fr]: 'guide',
	[WebLocale.in]: 'guide',
	[WebLocale.it]: 'guida',
	[WebLocale.mx]: 'guia',
	[WebLocale.tr]: 'haberler',
	[WebLocale.uk]: 'guide',
	[WebLocale.us]: 'guide',

	// [WebLocale.ar]: 'guia',
	// [WebLocale.au]: 'guide',
	// [WebLocale.ca]: 'guide',
} as Record<EditorialLocale, string>;

export function isEditorialLocale(locale: WebLocale) {
	return editorialLocales.includes(locale);
}

function makeEditorialSubdomainHomeRoute(currentLocale: EditorialLocale): RouteConfig {
	return {
		path: `/${currentLocale}`,
		name: 'app.editorial.subdomain.home',
		component: () => import(/* webpackChunkName: "editorial" */ '@/features/editorial/pages/GuideHome.vue'),
		beforeEnter: async (_to: Route, _from: Route, next: NavigationGuardNext) => {
			const languageStore = useLanguageStore();
			await languageStore.fetchLocales();
			next();
		},
		meta: { tab: 'editorial', pageType: PageType.EDITORIAL, subdomain: Domain.EDITORIAL },
	};
}

////////////////////////
/* EDITORIAL ARTICLES */
////////////////////////

function makeEditorialSubdomainArticleRoute(currentLocale: EditorialLocale): RouteConfig {
	return {
		component: () => import(/* webpackChunkName: "editorial" */ '@/features/editorial/pages/EditorialArticle.vue'),
		name: 'app.editorial.subdomain.article',
		path: `/${currentLocale}/:slug`,
		meta: {
			tab: 'editorial',
			collectionType: CollectionType.EDITORIAL,
			context: ['filters'],
			pageType: PageType.EDITORIAL_ARTICLE,
			subdomain: Domain.EDITORIAL,
		},
	};
}

/////////////
/* AUTHORS */
/////////////
const authorSlugs = {
	[WebLocale.ar]: 'autor',
	[WebLocale.au]: 'author',
	[WebLocale.br]: 'autor',
	[WebLocale.ca]: 'author',
	[WebLocale.fr]: 'auteur',
	[WebLocale.de]: 'autor',
	[WebLocale.it]: 'autore',
	[WebLocale.mx]: 'autor',
	[WebLocale.nz]: 'author',
	[WebLocale.pt]: 'autor',
	[WebLocale.es]: 'autor',
	[WebLocale.uk]: 'author',
	[WebLocale.us]: 'author',
	[WebLocale.bs]: 'author',
	[WebLocale.in]: 'author',
	[WebLocale.tr]: 'yazar',
} as const;

type AuthorLocale = keyof typeof authorSlugs;

function makeAuthorRoute(currentLocale: AuthorLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${authorSlugs[currentLocale]}/:name`,
		name: 'app.user.author',
		component: () => import(/* webpackChunkName: "userprofile" */ '@/pages/UserProfile.vue'),
		meta: {
			tab: 'user-profile',
			hasFilterBar: false,
			pageType: PageType.USER_PROFILE,
			subdomain: Domain.EDITORIAL,
		},
	};
}

function isAuthorLocale(locale: WebLocale): locale is AuthorLocale {
	return locale in authorSlugs;
}

export const editorialLocales = [
	WebLocale.br,
	WebLocale.de,
	WebLocale.es,
	WebLocale.fr,
	WebLocale.in,
	WebLocale.it,
	WebLocale.mx,
	WebLocale.tr,
	WebLocale.uk,
	WebLocale.us,
];

/////////////////
/* MAKE ROUTES */
/////////////////
export type EditorialLocale = (typeof editorialLocales)[number];

/**
 * Generate the app routes for Authors, List Articles, and
 * News Articles.
 *
 * @param currentLocale
 */
export function makeEditorialRoutes(currentLocale: WebLocale): RouteConfig[] {
	/*
		**IMPORTANT**
		The route order is important, they are evaluated in order so
		watchout for overlapping :dyanmic route segments!
	*/

	const routes: RouteConfig[] = [];

	routes.push(makeEditorialSubdomainLandingRoute());

	if (isEditorialLocale(currentLocale)) {
		routes.push(makeEditorialSubdomainHomeRoute(currentLocale as EditorialLocale));
		routes.push(makeEditorialSubdomainArticleRoute(currentLocale as EditorialLocale));
	}

	// Author pages
	if (isAuthorLocale(currentLocale)) routes.push(makeAuthorRoute(currentLocale as AuthorLocale));

	return routes;
}
